import { SimplePricing, TicketTypeItemType } from "@ac-expo/api-types";
import { noop } from "lodash";
import { ReactNode, useMemo } from "react";
import { Button } from "../Button";
import { Separator } from "../Separator";
import { PricedElementList } from "./PricedElementList";

export type TicketTypeItem = Pick<TicketTypeItemType, "name" | "userType"> & {
  pricing: SimplePricing;
};

type EventViewTicketingProps = {
  ticketTypes: TicketTypeItem[];
  activityTicketTypes: TicketTypeItem[];
  badgeTypes: TicketTypeItem[];
  isTicketingVisible: boolean;
  openTicketing?: () => void;
  hasHtmlContent?: boolean;
  /** Rendered under the public prices: the Group unlock line, owned by the app. */
  groupUnlockLine?: ReactNode;
};

export function EventViewTicketingCard({
  ticketTypes,
  activityTicketTypes,
  badgeTypes,
  isTicketingVisible,
  openTicketing,
  hasHtmlContent,
  groupUnlockLine,
}: EventViewTicketingProps) {
  const onOpenTicketing = openTicketing ?? noop;

  function formatForPricedElementList(items: Array<TicketTypeItem>) {
    return items.map((item) => ({
      name: item.name,
      price: ((item.pricing?.price?.priceTTC ?? 0) / 100).toFixed(2),
    }));
  }

  const {
    formattedTicketTypes,
    formattedGroupTicketTypes,
    formattedBadgeTypes,
    formattedActivityTicketTypes,
  } = useMemo(
    () => ({
      // Group tickets are only returned to their members: list them apart from the regular prices.
      formattedTicketTypes: formatForPricedElementList(
        ticketTypes.filter((ticketType) => ticketType.userType !== "group")
      ),
      formattedGroupTicketTypes: formatForPricedElementList(
        ticketTypes.filter((ticketType) => ticketType.userType === "group")
      ),
      formattedBadgeTypes: formatForPricedElementList(badgeTypes),
      formattedActivityTicketTypes:
        formatForPricedElementList(activityTicketTypes),
    }),
    [activityTicketTypes, badgeTypes, ticketTypes]
  );

  if (!isTicketingVisible) return null;

  return (
    <div className="md:max-w-[500px] lg:sticky lg:top-20 lg:z-10 lg:min-w-80 xl:min-w-960">
      <div className="flex flex-col gap-8 bg-white p-8 pt-0 lg:sticky lg:top-5 lg:rounded-2xl lg:pt-8 lg:shadow-xl">
        {hasHtmlContent && (
          <div className="hidden flex-col gap-4 lg:flex">
            <h3 className="flex justify-center text-lg font-bold">
              Billetterie Organisateur
            </h3>
          </div>
        )}
        {(formattedTicketTypes.length > 0 || groupUnlockLine) && (
          <div className="hidden flex-col gap-4 lg:flex">
            {formattedTicketTypes.length > 0 && (
              <>
                <h5 className="flex justify-center font-bold">Billets</h5>
                <PricedElementList
                  elements={formattedTicketTypes}
                  openTicketing={onOpenTicketing}
                />
              </>
            )}
            {/* Alone when every ticket of the event is reserved to a group. */}
            {groupUnlockLine}
          </div>
        )}
        {formattedGroupTicketTypes.length > 0 && (
          <>
            {formattedTicketTypes.length > 0 && <Separator />}

            <div className="hidden flex-col gap-4 lg:flex">
              <h5 className="flex justify-center font-bold">
                Billets de votre groupe
              </h5>
              <PricedElementList
                elements={formattedGroupTicketTypes}
                openTicketing={onOpenTicketing}
              />
            </div>
          </>
        )}
        {badgeTypes.length > 0 && (
          <>
            {ticketTypes.length > 0 && <Separator />}

            <div className="hidden flex-col gap-4 lg:flex">
              <h5 className="flex justify-center font-bold">Badges</h5>
              <PricedElementList
                elements={formattedBadgeTypes}
                openTicketing={onOpenTicketing}
              />
            </div>
          </>
        )}
        {activityTicketTypes.length > 0 && (
          <>
            {(ticketTypes.length > 0 || badgeTypes.length > 0) && <Separator />}

            <div className="hidden flex-col gap-4 lg:flex">
              <h5 className="flex justify-center font-bold">
                Billets animation
              </h5>
              <PricedElementList
                elements={formattedActivityTicketTypes}
                openTicketing={onOpenTicketing}
              />
            </div>
          </>
        )}
        <Button
          onClick={onOpenTicketing}
          variant="blue"
          className="w-full"
          size="lg"
        >
          Accéder à la billetterie
        </Button>
      </div>
    </div>
  );
}
